/**
 * "Fornecedor X vence os selecionados" (2026-10-02). Só aparecem fornecedores
 * com valor em ao menos um item; os itens em que ele não cotou, não tem ou
 * está sem valor ficam como estão — o backend devolve quais, e a tela avisa.
 */
import React, { useMemo, useState } from "react";
import { FiAward } from "react-icons/fi";

import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { escolherVencedorEmLote } from "../../../services/compras";
import { formatarReaisBR } from "../../../utils/dinheiro";
import { candidatosAVencedor, itemCotavel } from "./regrasDaCotacao";
import { ModalCaixa, ModalOverlay } from "./ui";

export function avisoDosIgnorados(ignorados = []) {
  if (!ignorados.length) return "";
  const lista = ignorados.map((i) => `${i.item_nome} (${i.motivo})`).join(", ");
  return `Ficaram como estavam: ${lista}.`;
}

export default function VencedorEmLote({ req, itens, onFechar, onSalvo }) {
  const { request } = useAuth();
  const toast = useToast();
  const candidatos = useMemo(() => candidatosAVencedor(itens), [itens]);
  const cotaveis = itens.filter(itemCotavel).length;
  const [escolhido, setEscolhido] = useState(candidatos[0]?.id || "");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function confirmar() {
    setSalvando(true);
    setErro("");
    try {
      const detalhe = await escolherVencedorEmLote(request, req.id, escolhido, itens.map((i) => i.id));
      const { aplicados = [], ignorados = [] } = detalhe?.vencedor_em_lote || {};
      const nome = candidatos.find((c) => c.id === escolhido)?.nome;
      toast.success(`${nome} vence ${aplicados.length} ${aplicados.length === 1 ? "item" : "itens"}.`);
      if (ignorados.length) toast.info(avisoDosIgnorados(ignorados));
      await onSalvo?.(detalhe);
    } catch (e) {
      setErro(e?.payload?.detalhe || e?.message || "Não foi possível escolher o vencedor.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <ModalOverlay onMouseDown={(e) => { if (e.target === e.currentTarget && !salvando) onFechar?.(); }}>
      <ModalCaixa role="dialog" aria-modal="true" aria-labelledby="vencedor-lote-titulo">
        <h3 id="vencedor-lote-titulo">
          <FiAward size={18} style={{ color: "var(--accent)" }} />
          Vencedor de {itens.length} {itens.length === 1 ? "item" : "itens"}
        </h3>
        {candidatos.length ? (
          <div style={{ display: "grid", gap: 6 }} role="radiogroup" aria-label="Fornecedor vencedor">
            {candidatos.map((c) => (
              <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                <input type="radio" name="vencedor-lote" checked={escolhido === c.id} onChange={() => setEscolhido(c.id)} disabled={salvando} />
                <span style={{ fontWeight: 600 }}>{c.nome}</span>
                <span style={{ color: "var(--ink-3)" }}>
                  cotou {c.itens} de {cotaveis} · {formatarReaisBR(c.total)}
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p>Nenhum fornecedor tem valor nos itens selecionados. Registre as cotações primeiro.</p>
        )}
        <p>Os itens em que o fornecedor não cotou ou não tem ficam como estão.</p>
        {erro ? <p role="alert" style={{ color: "var(--danger-text)" }}>{erro}</p> : null}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn--secondary" onClick={onFechar} disabled={salvando}>Voltar</button>
          <button type="button" className="btn btn--primary" onClick={confirmar} disabled={salvando || !escolhido}>
            {salvando ? <MiniSpinner size={13} label="Aguarde" /> : null}
            Escolher vencedor
          </button>
        </div>
      </ModalCaixa>
    </ModalOverlay>
  );
}
